import { useState } from 'react';
import { Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useSubscription, type SubscriptionTier } from '@/hooks/useSubscription';
import { cn } from '@/lib/utils';

interface SoftUpgradePromptProps {
  feature: string;
  message: string;
  currentUsage?: number;
  limit?: number;
  className?: string;
}

const NEXT_TIER: Record<SubscriptionTier, { name: string; price: string; features: string[] } | null> = {
  free: {
    name: 'Pro',
    price: '$29/month',
    features: [
      'Up to 10 strategy deployments',
      'Unlimited signal approvals',
      'Unlimited CSV exports & reports',
      'Live trading with up to 3 brokers',
    ],
  },
  pro: {
    name: 'Quant',
    price: '$79/month',
    features: [
      'Unlimited strategies & brokers',
      'AI signal auto-execution',
      'Priority support',
      'Advanced analytics',
    ],
  },
  quant: null,
};

export const SoftUpgradePrompt = ({ feature, message, currentUsage, limit, className }: SoftUpgradePromptProps) => {
  const [expanded, setExpanded] = useState(false);
  const { tier } = useSubscription();
  const nextTier = NEXT_TIER[tier];

  return (
    <div className={cn(
      'rounded-md border border-primary/20 bg-primary/5 px-3 py-2 max-h-fit transition-all',
      className
    )}>
      {/* Compact row */}
      <div className="flex items-center gap-2">
        <Lock className="w-3.5 h-3.5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-primary leading-tight">
            {currentUsage !== undefined && limit !== undefined
              ? `You've used ${currentUsage} of ${limit} ${feature} this month`
              : message}
          </p>
        </div>
        {nextTier && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] text-primary/70 hover:text-primary underline whitespace-nowrap flex items-center gap-0.5"
          >
            See what's included
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      {/* Expandable panel */}
      {expanded && nextTier && (
        <div className="mt-2 pt-2 border-t border-primary/10 space-y-2 animate-fade-in">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Current: {tier.toUpperCase()}</span>
            <span className="text-xs font-semibold text-primary">{nextTier.name} — {nextTier.price}</span>
          </div>
          <ul className="space-y-1">
            {nextTier.features.map((f) => (
              <li key={f} className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                <span className="text-primary mt-0.5">•</span>
                {f}
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            variant="outline"
            className="w-full h-7 text-xs border-primary/30 text-primary hover:bg-primary/10"
            onClick={() => {
              toast({
                title: 'Coming soon',
                description: 'Contact support@autotradeplug.com to upgrade your plan.',
              });
            }}
          >
            Upgrade to {nextTier.name}
          </Button>
        </div>
      )}
    </div>
  );
};
